import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Edit2, Trash2, Save, X, Tag } from 'lucide-react';
import supabase from '../supabase';

const GenreManager = () => {
  const [genres, setGenres] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newGenre, setNewGenre] = useState('');
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const genresSectionRef = useRef(null);

  const loadGenres = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('genres')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error loading genres:', error);
        console.error('Error details:', error.message);
        setGenres([]);
        return;
      }

      if (data && data.length > 0) {
        setGenres(data);
      } else {
        // Initialize with default genres if table is empty
        await initializeDefaultGenres();
      }
    } catch (error) {
      console.error('Error loading genres:', error);
      setGenres([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load genres from Supabase on component mount
  useEffect(() => {
    loadGenres();
  }, [loadGenres]);

  const initializeDefaultGenres = async () => {
    const defaultGenres = [
      'Punk', 'Rock N Roll', 'Synth', 'Electropunk', 'Goth', 
      'Post Punk', 'Death Rock', 'Darkwave', 'Oi', 'Hardcore', 
      'Crust', 'D-Beat', 'New Wave', 'Glam', 'Power Pop'
    ].sort().map(name => ({ name }));

    try {
      const { data, error } = await supabase
        .from('genres')
        .insert(defaultGenres)
        .select();

      if (error) throw error;
      setGenres(data || []);
    } catch (error) {
      console.error('Error initializing default genres:', error);
    }
  };

  const handleAddGenre = async () => {
    const trimmedGenre = newGenre.trim();
    if (trimmedGenre && !genres.some(g => g.name === trimmedGenre)) {
      try {
        const { data, error } = await supabase
          .from('genres')
          .insert([{ name: trimmedGenre }])
          .select();

        if (error) throw error;

        if (data && data[0]) {
          setGenres(prev => [...prev, data[0]].sort((a, b) => a.name.localeCompare(b.name)));
          setNewGenre('');
          setShowAddForm(false);
        }
      } catch (error) {
        console.error('Error adding genre:', error);
        console.error('Error details:', error.message);
        alert(`Failed to add genre: ${error.message}`);
      }
    }
  };

  const handleEditGenre = (genre) => {
    setEditingId(genre.id);
    setEditValue(genre.name);
  };

  const handleSaveEdit = async (id) => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && !genres.some(g => g.name === trimmedValue && g.id !== id)) {
      try {
        const { data, error } = await supabase
          .from('genres')
          .update({ name: trimmedValue })
          .eq('id', id)
          .select();

        if (error) throw error;

        if (data && data[0]) {
          setGenres(prev => prev.map(g => g.id === id ? data[0] : g).sort((a, b) => a.name.localeCompare(b.name)));
          setEditingId(null);
          setEditValue('');
        }
      } catch (error) {
        console.error('Error updating genre:', error);
        alert('Failed to update genre. Please try again.');
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleDeleteGenre = async (genre) => {
    if (window.confirm('Are you sure you want to delete this genre? This may affect existing events.')) {
      try {
        const { error } = await supabase
          .from('genres')
          .delete()
          .eq('id', genre.id);

        if (error) throw error;

        setGenres(prev => prev.filter(g => g.id !== genre.id));
      } catch (error) {
        console.error('Error deleting genre:', error);
        alert('Failed to delete genre. Please try again.');
      }
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    setNewGenre('');
  };

  const handleAddFirstGenre = () => {
    setShowAddForm(true);
    // Scroll to top of the Manage Genres section
    if (genresSectionRef.current) {
      genresSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div 
      ref={genresSectionRef}
      style={{
        background: 'rgba(255, 255, 255, 0.05)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '16px',
        padding: '2rem'
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h2 style={{color: 'white', fontSize: '1.75rem', margin: '0 0 0.5rem 0'}}>
            Manage Genres
          </h2>
          <p style={{color: '#9ca3af', margin: 0}}>
            Add, edit, or remove music genres for your events
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          style={{
            background: 'linear-gradient(45deg, #10b981, #059669)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            padding: '0.75rem 1.5rem',
            fontSize: '0.95rem',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.3s ease'
          }}
        >
          <Plus size={18} />
          Add Genre
        </button>
      </div>

      {/* Add Genre Form */}
      {showAddForm && (
        <div style={{
          background: 'rgba(16, 185, 129, 0.1)',
          border: '1px solid rgba(16, 185, 129, 0.2)',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem'
        }}>
          <h3 style={{color: '#10b981', fontSize: '1.1rem', margin: '0 0 1rem 0'}}>
            Add New Genre
          </h3>
          <div style={{
            display: 'flex',
            gap: '1rem',
            alignItems: 'flex-end'
          }}>
            <div style={{flex: 1}}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontWeight: '600',
                color: 'white',
                fontSize: '0.9rem'
              }}>
                Genre Name
              </label>
              <input
                type="text"
                value={newGenre}
                onChange={(e) => setNewGenre(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddGenre()}
                placeholder="Enter genre name"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '8px',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'white',
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{display: 'flex', gap: '0.5rem'}}>
              <button
                onClick={handleAddGenre}
                disabled={!newGenre.trim() || genres.some(g => g.name === newGenre.trim())}
                style={{
                  background: 'linear-gradient(45deg, #10b981, #059669)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  cursor: newGenre.trim() && !genres.some(g => g.name === newGenre.trim()) ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: newGenre.trim() && !genres.some(g => g.name === newGenre.trim()) ? 1 : 0.5
                }}
              >
                <Save size={16} />
              </button>
              <button
                onClick={handleCancelAdd}
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <X size={16} />
              </button>
            </div>
          </div>
          {newGenre.trim() && genres.some(g => g.name === newGenre.trim()) && (
            <p style={{
              color: '#ef4444',
              fontSize: '0.875rem',
              margin: '0.5rem 0 0 0',
              fontStyle: 'italic'
            }}>
              This genre already exists
            </p>
          )}
        </div>
      )}

      {/* Genres Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: 'rgba(255, 0, 128, 0.1)',
          border: '1px solid rgba(255, 0, 128, 0.2)',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '48px',
            height: '48px',
            background: 'linear-gradient(45deg, #ff0080, #7928ca)',
            borderRadius: '12px',
            color: 'white',
            margin: '0 auto 0.75rem'
          }}>
            <Tag size={20} />
          </div>
          <h3 style={{fontSize: '1.75rem', fontWeight: '900', color: 'white', margin: '0 0 0.25rem 0'}}>
            {genres.length}
          </h3>
          <p style={{color: '#9ca3af', fontWeight: '500', margin: 0}}>
            Total Genres
          </p>
        </div>
      </div>

      {/* Genres List */}
      {loading ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          color: 'white'
        }}>
          Loading genres...
        </div>
      ) : genres.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem 2rem',
          background: 'rgba(255, 255, 255, 0.02)',
          border: '2px dashed rgba(255, 255, 255, 0.1)',
          borderRadius: '12px'
        }}>
          <Tag size={48} style={{color: '#6b7280', marginBottom: '1rem'}} />
          <h3 style={{color: 'white', fontSize: '1.25rem', marginBottom: '0.5rem'}}>
            No genres yet
          </h3>
          <p style={{color: '#9ca3af', marginBottom: '1.5rem'}}>
            Add your first genre to get started
          </p>
          <button
            onClick={handleAddFirstGenre}
            style={{
              background: 'linear-gradient(45deg, #10b981, #059669)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <Plus size={18} />
            Add First Genre
          </button>
        </div>
      ) : (
        <div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '1rem'
          }}>
            {genres.map((genre) => (
              <div
                key={genre.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  padding: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  transition: 'all 0.3s ease'
                }}
              >
                {editingId === genre.id ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    flex: 1
                  }}>
                    <input
                      type="text"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleSaveEdit(genre.id);
                        if (e.key === 'Escape') handleCancelEdit();
                      }}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: '6px',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '0.95rem'
                      }}
                      autoFocus
                    />
                    <button
                      onClick={() => handleSaveEdit(genre.id)}
                      disabled={!editValue.trim() || (editValue.trim() !== genre.name && genres.some(g => g.name === editValue.trim()))}
                      style={{
                        background: 'linear-gradient(45deg, #10b981, #059669)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '0.5rem',
                        cursor: editValue.trim() && (editValue.trim() === genre.name || !genres.some(g => g.name === editValue.trim())) ? 'pointer' : 'not-allowed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: editValue.trim() && (editValue.trim() === genre.name || !genres.some(g => g.name === editValue.trim())) ? 1 : 0.5
                      }}
                    >
                      <Save size={14} />
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      style={{
                        background: 'rgba(239, 68, 68, 0.2)',
                        color: '#ef4444',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        borderRadius: '6px',
                        padding: '0.5rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      flex: 1
                    }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'linear-gradient(45deg, #ff0080, #7928ca)'
                      }} />
                      <span style={{
                        color: 'white',
                        fontSize: '0.95rem',
                        fontWeight: '500'
                      }}>
                        {genre.name}
                      </span>
                    </div>
                    <div style={{display: 'flex', gap: '0.5rem'}}>
                      <button
                        onClick={() => handleEditGenre(genre)}
                        style={{
                          background: 'rgba(59, 130, 246, 0.2)',
                          color: '#3b82f6',
                          border: '1px solid rgba(59, 130, 246, 0.3)',
                          borderRadius: '6px',
                          padding: '0.4rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Edit2 size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteGenre(genre)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.2)',
                          color: '#ef4444',
                          border: '1px solid rgba(239, 68, 68, 0.3)',
                          borderRadius: '6px',
                          padding: '0.4rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenreManager;
